import React, { FC, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Empty,
  Modal,
  Paging,
  Radio,
  Select,
  Wrapper,
  useBreadcrumbRoutes,
  useSearchParams,
} from '@maxtropy/components';
import { ExpandOutlined } from '@ant-design/icons';
import { Spin } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  apiV2InspectionRecorderDetailPost,
  apiV2InspectionRecorderListPost,
  apiV2InspectionRecorderPagePost,
} from '@maxtropy/device-customer-apis-v2';
import { useHasPermission } from '@/utils/utils';
import { PermissionsType } from '@/common/permissionsConst';
import styles from './index.module.scss';
import RecorderPreview, { RecorderPreviewItem } from '@/components/RecorderPreview';

type RecorderItem = RecorderPreviewItem;

const layoutOptions = [
  { key: '2x2', label: '2X2', columns: 2, pageSize: 4 },
  { key: '3x3', label: '3X3', columns: 3, pageSize: 9 },
  { key: '4x4', label: '4X4', columns: 4, pageSize: 16 },
  { key: '4x3', label: '4X3', columns: 4, pageSize: 12 },
  { key: '5x3', label: '5X3', columns: 5, pageSize: 15 },
];

const InspectionRecorderView: FC = () => {
  const navigate = useNavigate();
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [loading, setLoading] = useState(false);
  const [recorderList, setRecorderList] = useState<RecorderItem[]>([]);
  const [recorderOptions, setRecorderOptions] = useState<Array<{ label: string; value: number }>>([]);
  const [selectedRecorderIds, setSelectedRecorderIds] = useState<number[]>([]);
  const [layoutKey, setLayoutKey] = useState('2x2');
  const [fullScreenItem, setFullScreenItem] = useState<RecorderItem>();
  const [fullScreenSize, setFullScreenSize] = useState({ width: 960, height: 540 });
  const hasConfigPermission = useHasPermission(PermissionsType.B_PATROLRECORDERVIDEOCONFIGURATION);

  const currentLayout = useMemo(() => {
    return layoutOptions.find(option => option.key === layoutKey) || layoutOptions[0];
  }, [layoutKey]);

  const pagingInfo = useSearchParams(currentLayout.pageSize, {});
  const { pageOffset, pageSize, setPageOffset, setPageSize, setTotalCount } = pagingInfo;

  useEffect(() => {
    apiV2InspectionRecorderListPost({}).then(res => {
      const options = (res?.list ?? [])
        .filter(item => item.id != null && item.deviceName)
        .map(item => ({
          label: item.deviceName!,
          value: item.id!,
        }));
      setRecorderOptions(options);
    });
  }, []);

  useEffect(() => {
    if (pageSize !== currentLayout.pageSize) {
      setPageOffset(1);
      setPageSize(currentLayout.pageSize);
    }
  }, [currentLayout.pageSize, pageSize, setPageOffset, setPageSize]);

  useEffect(() => {
    setLoading(true);
    if (selectedRecorderIds.length > 0) {
      Promise.all(selectedRecorderIds.map(id => apiV2InspectionRecorderDetailPost({ id })))
        .then(results => {
          const list = results.filter(item => item?.id) as RecorderItem[];
          setRecorderList(list);
          setTotalCount(list.length);
        })
        .finally(() => setLoading(false));
      return;
    }

    apiV2InspectionRecorderPagePost({
      page: pageOffset,
      size: currentLayout.pageSize,
    })
      .then(res => {
        setRecorderList((res?.list ?? []) as RecorderItem[]);
        setTotalCount(Number(res?.total) || 0);
      })
      .finally(() => setLoading(false));
  }, [currentLayout.pageSize, pageOffset, selectedRecorderIds, setTotalCount]);

  useEffect(() => {
    if (!fullScreenItem) {
      return;
    }
    const updateModalSize = () => {
      const maxWidth = Math.floor(window.innerWidth * 0.72);
      const maxHeight = Math.floor(window.innerHeight * 0.72);
      const ratio = 16 / 9;
      const widthByHeight = Math.floor(maxHeight * ratio);
      const nextWidth = Math.min(maxWidth, widthByHeight);
      const nextHeight = Math.floor(nextWidth / ratio);
      setFullScreenSize({ width: nextWidth, height: nextHeight });
    };
    updateModalSize();
    window.addEventListener('resize', updateModalSize);
    return () => window.removeEventListener('resize', updateModalSize);
  }, [fullScreenItem]);

  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes ?? [])]}>
      <div className={styles.page}>
        <div className={styles.toolbar}>
          <div className={styles.toolbarLeft}>
            <div className={styles.filterGroup}>
              <span className={styles.label}>巡检记录仪名称</span>
              <Select
                allowClear
                mode="multiple"
                showSearch
                optionFilterProp="label"
                placeholder="请选择"
                className={styles.select}
                value={selectedRecorderIds}
                options={recorderOptions}
                onChange={value => {
                  const ids = ((value as Array<number | string>) || [])
                    .map(item => Number(item))
                    .filter(item => !Number.isNaN(item));
                  setSelectedRecorderIds(ids);
                  setPageOffset(1);
                }}
              />
            </div>

            <div className={styles.layoutGroup}>
              <span className={styles.label}>布局:</span>
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                className={styles.layoutRadio}
                value={layoutKey}
                options={layoutOptions.map(item => ({ label: item.label, value: item.key }))}
                onChange={e => {
                  setLayoutKey(e.target.value);
                  setPageOffset(1);
                }}
              />
            </div>
          </div>

          {hasConfigPermission && (
            <Button
              className={styles.configBtn}
              type="primary"
              onClick={() => navigate('/monitor/video/inspectionView/config')}
            >
              配置
            </Button>
          )}
        </div>

        <Spin spinning={loading}>
          {recorderList.length === 0 ? (
            <div className={styles.emptyState}>
              <Empty description="暂无视频" />
            </div>
          ) : (
            <div
              className={styles.grid}
              style={{
                gridTemplateColumns: `repeat(${currentLayout.columns}, minmax(0, 1fr))`,
              }}
            >
              {recorderList.map((item, index) => (
                <div key={`${item.id ?? index}-${item.deviceName ?? index}`} className={styles.card}>
                  <div className={styles.previewWrap} style={{ aspectRatio: '16 / 9' }}>
                    <RecorderPreview item={item} hkDomPrefix="hk_inspection" />
                  </div>
                  <div className={styles.cardFooter}>
                    <span className={styles.cardTitle}>{item.deviceName || `巡检记录仪${index + 1}`}</span>
                    <Button
                      type="link"
                      className={styles.fullscreenBtn}
                      icon={<ExpandOutlined />}
                      onClick={() => setFullScreenItem(item)}
                    >
                      全屏
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Spin>

        {!loading && selectedRecorderIds.length === 0 && (
          <div className={styles.paginationBar}>
            <Paging pagingInfo={pagingInfo} showSizeChanger={false} />
          </div>
        )}

        {fullScreenItem && (
          <Modal
            open={!!fullScreenItem}
            title={fullScreenItem.deviceName || '巡检记录仪视频'}
            width={fullScreenSize.width}
            footer={null}
            destroyOnClose
            onCancel={() => setFullScreenItem(undefined)}
          >
            <div style={{ height: fullScreenSize.height }}>
              <RecorderPreview item={fullScreenItem} hkDomPrefix="hk_inspection_fullscreen" />
            </div>
          </Modal>
        )}
      </div>
    </Wrapper>
  );
};

export default InspectionRecorderView;
